import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Layout } from './components/layout/Layout';
import { ToastContainer } from './components/ui/Toast';
import { usePublicSettings } from './hooks/useApi';
import { Account } from './pages/Account';
import { Assignments } from './pages/Assignments';
import { Audit } from './pages/Audit';
import { CarDetail } from './pages/CarDetail';
import { Cars } from './pages/Cars';
import { Contracts } from './pages/Contracts';
import { Dashboard } from './pages/Dashboard';
import { Documents } from './pages/Documents';
import { Drivers } from './pages/Drivers';
import { Expenses } from './pages/Expenses';
import { Fines } from './pages/Fines';
import { Fuel } from './pages/Fuel';
import { ForcePasswordChange } from './pages/ForcePasswordChange';
import { GPS } from './pages/GPS';
import { Login } from './pages/Login';
import { Maintenance } from './pages/Maintenance';
import { NotFound } from './pages/NotFound';
import { Reservations } from './pages/Reservations';
import { Services } from './pages/Services';
import { Settings } from './pages/Settings';
import { TileHome } from './pages/TileHome';
import { Tires } from './pages/Tires';
import { Vignettes } from './pages/Vignettes';
import { authApi, onUnauthorized } from './services/api';
import { isLanguage, t, useLanguageStore } from './i18n';
import { toast, useAuthStore, useViewModeStore } from './store';
import { applyPrimaryColor } from './utils/helpers';

let sessionChecked = false;

function RequireAuth() {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  if (user.must_change_password) return <ForcePasswordChange />;
  return <Outlet />;
}

/** Home page: the overview, or the icon grid in the tile view. */
function Home() {
  const tiles = useViewModeStore((s) => s.viewMode === 'tiles');
  return tiles ? <TileHome /> : <Dashboard />;
}

function RequireAdmin() {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  return isAdmin ? <Outlet /> : <Navigate to="/" replace />;
}

/** Keeps the stored user in sync with the server session and applies branding. */
function useSessionAndBranding() {
  const setUser = useAuthStore((s) => s.setUser);
  const hasUser = useAuthStore((s) => !!s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: branding } = usePublicSettings();

  useEffect(() => {
    onUnauthorized(() => {
      if (!useAuthStore.getState().user) return;
      setUser(null);
      queryClient.clear();
      toast.warning(t('Platnosť prihlásenia vypršala, prihláste sa prosím znova'));
      navigate('/login', { replace: true });
    });
  }, [setUser, navigate, queryClient]);

  useEffect(() => {
    // Validate the persisted session once on start (role or name may have changed)
    // and use the language saved with the account (it may have been changed on another device).
    // Once per page load: the app is rendered again when the language changes.
    if (hasUser && !sessionChecked)
      authApi
        .me()
        .then((user) => {
          sessionChecked = true;
          setUser(user);
          if (isLanguage(user.language)) useLanguageStore.getState().setLanguage(user.language);
        })
        .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyPrimaryColor(branding?.primary_color);
    if (branding?.company_name) document.title = branding.company_name;
  }, [branding]);
}

export function App() {
  useSessionAndBranding();

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <>
            <Login />
            <ToastContainer />
          </>
        }
      />
      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="overview" element={<Dashboard />} />
          <Route path="cars" element={<Cars />} />
          <Route path="cars/:id" element={<CarDetail />} />
          <Route path="drivers" element={<Drivers />} />
          <Route path="reservations" element={<Reservations />} />
          <Route path="assignments" element={<Assignments />} />
          <Route path="contracts" element={<Contracts />} />
          <Route path="maintenance" element={<Maintenance />} />
          <Route path="services" element={<Services />} />
          <Route path="tires" element={<Tires />} />
          <Route path="documents" element={<Documents />} />
          <Route path="vignettes" element={<Vignettes />} />
          <Route path="fines" element={<Fines />} />
          <Route path="fuel" element={<Fuel />} />
          <Route path="expenses" element={<Expenses />} />
          <Route path="gps" element={<GPS />} />
          <Route path="account" element={<Account />} />
          <Route element={<RequireAdmin />}>
            <Route path="audit" element={<Audit />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
